import { useGraderProgressTableColumns, useStudentStatisticsTableColumns } from 'components/GradingInsights/Table';
import { useSubmissionsTableColumns } from 'components/Submissions/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { omit } from 'lodash';
import {
    calculateClassPerformanceChartData,
    calculateClassPerformanceStats,
    calculateDetailedGradingData,
    calculateGraderStats,
    calculateStudentLevelStats,
    calculateTimeSavedStats,
    getMultiselectFilterValue,
} from 'pages/Submissions/gradingInsights';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import assignmentService from 'services/assignment';
import submissionService from 'services/submission';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { FilterOption, Placeholder, SortOrder, SubmissionTabs } from 'utils/constants';
import {
    exportGraderStatisticsToCSV,
    exportGradingStatisticsToCSV,
    exportSubmissionsToCSV,
    getLatestConfirmedId,
    getSuggestedGradeCriteria,
    getUserPreferredFullName,
} from 'utils/helper';

const PageContext = createContext();

const handleFilters = (allData, filters, setSortedFilteredData) => {
    let res = [...allData];

    res = res.filter((submission) => {
        const searchCond = submission.files[0].name.toLowerCase().includes(filters.search.toLowerCase());
        const statusCond = !filters.status.length
            ? true
            : filters.status.some((status) => {
                  if (status === 'ready') {
                      return submission.ai && !submission.grading;
                  }
                  if (status === 'reviewed') {
                      return submission.ai && submission.grading;
                  }
                  return false;
              });
        return searchCond && statusCond;
    });

    setSortedFilteredData(res);
};

const PageProvider = (props) => {
    const { course, accountUsers, instructorAccountUsers, currentAssignment, submissions, setSubmissions, setBatch } =
        useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');

    const ownerAccountId = auth?.owner_account_id;
    const graderColumns = useGraderProgressTableColumns();
    const studentStatisticsColumns = useStudentStatisticsTableColumns();

    const columns = useSubmissionsTableColumns();

    const [filters, setFilters] = useState({
        open: false,
        status: [],
        search: '',
    });
    const [activeTab, setActiveTab] = useState(SubmissionTabs.SUBMISSIONS);

    // Grading Progress States
    const [graderSortState, setGraderSortState] = useState({ order: SortOrder.DESC, orderBy: graderColumns[1].id });
    const [predictedGradingTime, setPredictedGradingTime] = useState('');
    const [calculatedTimeSavedPerSub, setCalculatedTimeSavedPerSub] = useState(Placeholder.NO_DATA);
    const [totalTimeSaved, setTotalTimeSaved] = useState(Placeholder.NO_DATA);

    // Student Performance States
    const [performanceFilters, setPerformanceFilters] = useState({
        criterion: FilterOption.ALL,
        grader: FilterOption.ALL,
    });

    // Student Statistics States
    const [isTableExpanded, setIsTableExpanded] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(() =>
        studentStatisticsColumns.filter((col) => col.key !== 'name').map((col) => col.key)
    );
    const [studentSortState, setStudentSortState] = useState({
        order: SortOrder.ASC,
        orderBy: studentStatisticsColumns[0].id,
    });
    const [statisticsFilters, setStatisticsFilters] = useState({
        criterion: FilterOption.ALL,
        grader: FilterOption.ALL,
    });

    const extras = useMemo(
        () => ({
            assignment: currentAssignment,
            accountUsers,
        }),
        [currentAssignment, accountUsers]
    );

    const submissionsData = useMemo(() => submissions || [], [submissions]);

    const {
        sortedFilteredData: sortedFilteredSubmissions,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(
        submissionsData,
        columns,
        filters,
        setSubmissions,
        handleFilters,
        extras
    );

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            status: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const resetSubmissions = useCallback(() => {
        setDialog({
            open: true,
            message: t('dialog.erase-assignment-data.title', 'Erase Existing Assignment Data?'),
            description: t(
                'dialog.erase-assignment-data.description',
                'If you reset, any existing submissions and associated grades/feedback will be permanently deleted.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setSubmissions([]);
                setBatch([]);
                closeDialog();
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(currentAssignment.id),
                    await assignmentService.deleteAssignmentBatch(currentAssignment.id),
                ]);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    }, [closeDialog, currentAssignment?.id, setBatch, setSubmissions, setDialog, t]);

    const handleCalculateTimeSave = useCallback(() => {
        const { savedPerSub, totalSaved } = calculateTimeSavedStats(submissions, predictedGradingTime);
        setCalculatedTimeSavedPerSub(savedPerSub);
        setTotalTimeSaved(totalSaved);
    }, [submissions, predictedGradingTime]);

    const handleResetTimeSave = useCallback(() => {
        setPredictedGradingTime('');
        setCalculatedTimeSavedPerSub(Placeholder.NO_DATA);
        setTotalTimeSaved(Placeholder.NO_DATA);
    }, []);

    const handleColumnToggle = useCallback((columnKey) => {
        setVisibleColumns((prev) => {
            if (prev.includes(columnKey) && prev.length === 1) return prev;
            return prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey];
        });
    }, []);

    const handleSetGraderSortState = useCallback((columnId) => {
        setGraderSortState((prev) => ({
            orderBy: columnId,
            order: prev.orderBy === columnId && prev.order === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC,
        }));
    }, []);

    const handleSetStudentSortState = useCallback((columnId) => {
        setStudentSortState((prev) => ({
            orderBy: columnId,
            order: prev.orderBy === columnId && prev.order === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC,
        }));
    }, []);

    const handleStatisticsGraderFilter = useCallback((clickedOption) => {
        setStatisticsFilters((prev) => ({
            ...prev,
            grader: getMultiselectFilterValue(prev.grader, clickedOption),
        }));
    }, []);

    const handlePerformanceGraderFilter = useCallback((clickedOption) => {
        setPerformanceFilters((prev) => ({
            ...prev,
            grader: getMultiselectFilterValue(prev.grader, clickedOption),
        }));
    }, []);

    const gradersList = useMemo(() => {
        return (
            instructorAccountUsers?.map((user) => ({
                id: user.owner_account_id,
                name: getUserPreferredFullName(user),
            })) || []
        );
    }, [instructorAccountUsers]);

    const criteriaList = useMemo(() => {
        return (
            currentAssignment?.rubric?.criteria?.map((criterion) => ({
                id: criterion.id,
                title: criterion.title,
            })) || []
        );
    }, [currentAssignment]);

    const performanceFilterOptions = useMemo(
        () => ({
            criteria: [{ id: FilterOption.ALL, title: t('submission.overall-scores-label') }, ...criteriaList],
            graders: [{ id: FilterOption.ALL, name: t('submission.all-graders-label') }, ...gradersList],
        }),
        [criteriaList, gradersList, t]
    );

    const filteredClassSubmissions = useMemo(() => {
        if (!submissions) return [];

        let res = submissions.filter((s) => s.grading);

        if (performanceFilters.grader !== FilterOption.ALL) {
            const activeGraderId = performanceFilters.grader;
            if (Array.isArray(activeGraderId)) {
                res = res.filter((s) => activeGraderId.includes(getLatestConfirmedId(s.sessions)));
            } else {
                res = res.filter((s) => getLatestConfirmedId(s.sessions) === activeGraderId);
            }
        }
        return res;
    }, [submissions, performanceFilters.grader]);

    const gradingProgress = useMemo(() => {
        const total = submissions?.length || 0;
        const graded = submissions?.filter((s) => s.grading).length || 0;
        return {
            total,
            graded,
            percentage: total > 0 ? Math.round((graded / total) * 100) : 0,
        };
    }, [submissions]);

    const classPerformanceStats = useMemo(
        () => calculateClassPerformanceStats(filteredClassSubmissions, currentAssignment, performanceFilters),
        [filteredClassSubmissions, performanceFilters, currentAssignment]
    );

    const classPerformanceChartData = useMemo(
        () =>
            calculateClassPerformanceChartData(
                submissions || [],
                filteredClassSubmissions,
                currentAssignment,
                performanceFilters,
                gradersList
            ),
        [submissions, filteredClassSubmissions, currentAssignment, performanceFilters, gradersList]
    );

    const detailedGradingData = useMemo(
        () => calculateDetailedGradingData(submissions || [], currentAssignment, accountUsers),
        [submissions, currentAssignment, accountUsers]
    );

    const studentGradingStatistics = useMemo(
        () => calculateStudentLevelStats(detailedGradingData, statisticsFilters),
        [detailedGradingData, statisticsFilters]
    );

    const calculatedGraders = useMemo(
        () => calculateGraderStats(instructorAccountUsers, submissions || [], currentAssignment),
        [instructorAccountUsers, submissions, currentAssignment]
    );

    const sortedGraders = useMemo(() => {
        const { orderBy, order } = graderSortState;
        if (!orderBy) return calculatedGraders;

        const column = graderColumns.find((col) => col.id === orderBy);
        return column?.comparator ? column.comparator(order, calculatedGraders) : calculatedGraders;
    }, [calculatedGraders, graderSortState, graderColumns]);

    const sortedStudentGradingStatistics = useMemo(() => {
        const { orderBy, order } = studentSortState;
        if (!orderBy) return studentGradingStatistics;

        const column = studentStatisticsColumns.find((col) => col.id === orderBy || col.key === orderBy);
        return column?.comparator ? column.comparator(order, studentGradingStatistics) : studentGradingStatistics;
    }, [studentGradingStatistics, studentSortState, studentStatisticsColumns]);

    const exportSubmissions = useCallback(() => {
        return exportSubmissionsToCSV(course, currentAssignment, submissions, accountUsers, t);
    }, [accountUsers, course, currentAssignment, submissions, t]);

    const exportStatistics = useCallback(() => {
        return exportGradingStatisticsToCSV(currentAssignment, detailedGradingData, t);
    }, [currentAssignment, detailedGradingData, t]);

    const exportGraderStatistics = useCallback(() => {
        return exportGraderStatisticsToCSV(currentAssignment, calculatedGraders, gradingProgress.total, t);
    }, [currentAssignment, calculatedGraders, gradingProgress.total, t]);

    const acceptableSuggestionEntries = useMemo(() => {
        return sortedFilteredSubmissions
            .filter((submission) => !submission.grading)
            .map((submission) => ({
                submission,
                criteria: getSuggestedGradeCriteria(submission),
            }))
            .filter(({ criteria }) => Object.keys(criteria).length > 0);
    }, [sortedFilteredSubmissions]);

    const canAcceptAllSuggestions = Boolean(ownerAccountId) && acceptableSuggestionEntries.length > 0;

    const handleAcceptAll = useCallback(() => {
        if (!acceptableSuggestionEntries.length || !ownerAccountId) return;

        setDialog({
            open: true,
            message: t('dialog.accept-all-suggestions.title', 'Confirm to Accept AI Grading Suggestions?'),
            description: t(
                'dialog.accept-all-suggestions.description',
                'This will assign the AI suggested grades to all non-approved submissions.'
            ),
            actionButtonText: t('common:controls.confirm', 'Confirm'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const now = Date.now();
                const updatedSubmissions = acceptableSuggestionEntries.map(({ submission, criteria }) => ({
                    ...submission,
                    modified_on: now,
                    sessions: {
                        ...submission.sessions,
                        [ownerAccountId]: [
                            ...(submission.sessions?.[ownerAccountId] || []),
                            {
                                created_on: now,
                                duration: 0,
                                reason: 'confirm',
                            },
                        ],
                    },
                    grading: {
                        criteria,
                    },
                }));
                const updatedSubmissionsById = new Map(
                    updatedSubmissions.map((submission) => [submission.id, submission])
                );

                await Promise.all(
                    updatedSubmissions.map((submission) =>
                        submissionService.updateSubmission(submission.id, omit(submission, ['_id', 'isDuplicate']))
                    )
                );
                setSubmissions((current) =>
                    current.map((submission) => updatedSubmissionsById.get(submission.id) || submission)
                );
                await new Promise((resolve) => setTimeout(resolve, 0));
                closeDialog();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    }, [acceptableSuggestionEntries, closeDialog, ownerAccountId, setDialog, setSubmissions, t]);

    const gradingInsightsData = useMemo(
        () => ({
            // Grading Progress & Time
            gradingProgress,
            predictedGradingTime,
            setPredictedGradingTime,
            calculatedTimeSavedPerSub,
            totalTimeSaved,
            handleCalculateTimeSave,
            handleResetTimeSave,
            sortedGraders,
            graderSortState,
            handleSetGraderSortState,
            exportGraderStatistics,
            graderColumns,

            // Performance Charts
            classPerformanceStats,
            classPerformanceChartData,
            performanceFilters,
            setPerformanceFilters,
            performanceFilterOptions,
            handlePerformanceGraderFilter,

            // Student Statistics
            studentGradingStatistics: sortedStudentGradingStatistics,
            studentSortState,
            handleSetStudentSortState,
            statisticsFilters,
            setStatisticsFilters,
            handleStatisticsGraderFilter,
            exportStatistics,
            visibleColumns,
            onColumnToggle: handleColumnToggle,
            isTableExpanded,
            onToggleTableExpanded: setIsTableExpanded,
            studentStatisticsColumns,
        }),
        [
            gradingProgress,
            predictedGradingTime,
            calculatedTimeSavedPerSub,
            totalTimeSaved,
            handleCalculateTimeSave,
            handleResetTimeSave,
            classPerformanceStats,
            classPerformanceChartData,
            performanceFilters,
            performanceFilterOptions,
            handlePerformanceGraderFilter,
            sortedGraders,
            graderSortState,
            handleSetGraderSortState,
            exportGraderStatistics,
            sortedStudentGradingStatistics,
            studentSortState,
            handleSetStudentSortState,
            statisticsFilters,
            handleStatisticsGraderFilter,
            exportStatistics,
            visibleColumns,
            handleColumnToggle,
            isTableExpanded,
            graderColumns,
            studentStatisticsColumns,
        ]
    );

    const contextValue = useMemo(
        () => ({
            assignment: currentAssignment,
            submissions,
            setSubmissions,
            exportSubmissions,
            resetSubmissions,
            sortedFilteredSubmissions,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            columns,
            activeTab,
            setActiveTab,
            gradingInsightsData,
            handleAcceptAll,
            canAcceptAllSuggestions,
        }),
        [
            currentAssignment,
            submissions,
            sortedFilteredSubmissions,
            sortState,
            handleSetSortState,
            filters,
            activeTab,
            gradingInsightsData,
            columns,
            exportSubmissions,
            resetFilters,
            resetSubmissions,
            setSubmissions,
            handleAcceptAll,
            canAcceptAllSuggestions,
        ]
    );
    return <PageContext.Provider value={contextValue}>{props.children}</PageContext.Provider>;
};

export { PageContext };
export default PageProvider;
